import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

function slugify(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function toPlayableVideoUrl(url) {
  if (!url) return "";

  try {
    const raw = url.trim();
    const u = new URL(raw);
    const hostname = u.hostname.replace(/^www\./, "").toLowerCase();
    const pathname = u.pathname.toLowerCase();

    const isDropbox =
      hostname === "dropbox.com" || hostname === "dl.dropboxusercontent.com";

    if (isDropbox) {
      u.hostname = "dl.dropboxusercontent.com";
      u.searchParams.delete("dl");
      u.searchParams.delete("raw");
      return u.toString();
    }

    const isGoogleDrive = hostname === "drive.google.com";

    if (isGoogleDrive) {
      const fileMatch = u.pathname.match(/\/file\/d\/([^/]+)/);
      if (fileMatch?.[1]) {
        const fileId = fileMatch[1];
        return `https://drive.google.com/uc?export=download&id=${fileId}`;
      }

      const id = u.searchParams.get("id");
      if (id) {
        return `https://drive.google.com/uc?export=download&id=${id}`;
      }

      throw new Error("Unsupported Google Drive link format.");
    }

    const isMp4 = pathname.endsWith(".mp4") || pathname.includes(".mp4");

    if (isMp4) {
      return raw;
    }

    throw new Error(
      "URL must be a direct MP4 link, Dropbox link, or Google Drive link.",
    );
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error("Invalid video URL.");
  }
}

function formatSeasonDisplay(season) {
  if (season?.season_label?.trim()) return season.season_label.trim();
  if (season?.season_number !== null && season?.season_number !== undefined) {
    return `Season ${season.season_number}`;
  }
  return "Untitled Season";
}

function normaliseCdnPath(path) {
  return String(path || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/\/+/g, "/");
}

function formatBlogDateForInput(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

export default function Admin() {
  const [session, setSession] = useState(null);

  // ===== MODE SWITCH =====
  const [mode, setMode] = useState("series");

  // ===== SERIES DATA =====
  const [shows, setShows] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [episodes, setEpisodes] = useState([]);

  // ===== MOVIES DATA =====
  const [movies, setMovies] = useState([]);

  // ===== BLOGS DATA =====
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingBlogPostId, setEditingBlogPostId] = useState(null);

  const [blogTitle, setBlogTitle] = useState("");
  const [blogSlug, setBlogSlug] = useState("");
  const [blogCategory, setBlogCategory] = useState("Product Update");
  const [blogAuthor, setBlogAuthor] = useState("");
  const [blogPublishedDate, setBlogPublishedDate] = useState("");
  const [blogReadTimeMinutes, setBlogReadTimeMinutes] = useState(4);
  const [blogOverview, setBlogOverview] = useState("");
  const [blogSummary, setBlogSummary] = useState("");
  const [blogContent, setBlogContent] = useState("");
  const [blogIsPublished, setBlogIsPublished] = useState(true);

  // ===== CDN DATA =====
  const [cdnFiles, setCdnFiles] = useState([]);
  const [cdnPath, setCdnPath] = useState("");
  const [cdnFile, setCdnFile] = useState(null);

  // ===== MANAGE: BANNERS =====
  const [banners, setBanners] = useState([]);
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerEnabled, setBannerEnabled] = useState(false);
  const [bannerVariant, setBannerVariant] = useState("info");
  const [bannerLinkUrl, setBannerLinkUrl] = useState("");
  const [bannerLinkText, setBannerLinkText] = useState("");

  const [editingBanner, setEditingBanner] = useState(null);
  const [editBannerMessage, setEditBannerMessage] = useState("");
  const [editBannerEnabled, setEditBannerEnabled] = useState(false);
  const [editBannerVariant, setEditBannerVariant] = useState("info");
  const [editBannerLinkUrl, setEditBannerLinkUrl] = useState("");
  const [editBannerLinkText, setEditBannerLinkText] = useState("");

  // auth form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // selection (series)
  const [selectedShowId, setSelectedShowId] = useState("");
  const [selectedSeasonId, setSelectedSeasonId] = useState("");

  // status
  const [status, setStatus] = useState("");

  // ===== SERIES CREATE =====
  const [showTitle, setShowTitle] = useState("");
  const [showDescription, setShowDescription] = useState("");
  const [showHeroImage, setShowHeroImage] = useState(null);

  const [seasonInput, setSeasonInput] = useState("");

  const [episodeNumber, setEpisodeNumber] = useState(1);
  const [episodeTitle, setEpisodeTitle] = useState("");
  const [episodeDescription, setEpisodeDescription] = useState("");
  const [duration, setDuration] = useState("");

  const [useExternalUrl, setUseExternalUrl] = useState(true);
  const [externalVideoUrl, setExternalVideoUrl] = useState("");
  const [videoFile, setVideoFile] = useState(null);

  const [thumbFile, setThumbFile] = useState(null);

  // ===== SERIES EDIT MODALS =====
  const [editingShow, setEditingShow] = useState(null);
  const [editingSeason, setEditingSeason] = useState(null);
  const [editingEpisode, setEditingEpisode] = useState(null);

  const [editShowTitle, setEditShowTitle] = useState("");
  const [editShowDescription, setEditShowDescription] = useState("");
  const [editShowHeroImage, setEditShowHeroImage] = useState(null);

  const [editSeasonInput, setEditSeasonInput] = useState("");

  const [editEpisodeNumber, setEditEpisodeNumber] = useState(1);
  const [editEpisodeTitle, setEditEpisodeTitle] = useState("");
  const [editEpisodeDescription, setEditEpisodeDescription] = useState("");
  const [editDuration, setEditDuration] = useState("");
  const [editUseExternalUrl, setEditUseExternalUrl] = useState(true);
  const [editExternalVideoUrl, setEditExternalVideoUrl] = useState("");
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editThumbFile, setEditThumbFile] = useState(null);

  // ===== MOVIES CREATE =====
  const [movieTitle, setMovieTitle] = useState("");
  const [movieDescription, setMovieDescription] = useState("");
  const [movieDuration, setMovieDuration] = useState("");
  const [movieHeroImage, setMovieHeroImage] = useState(null);
  const [movieThumbFile, setMovieThumbFile] = useState(null);

  const [movieUseExternalUrl, setMovieUseExternalUrl] = useState(true);
  const [movieExternalVideoUrl, setMovieExternalVideoUrl] = useState("");
  const [movieVideoFile, setMovieVideoFile] = useState(null);

  // ===== MOVIES EDIT =====
  const [editingMovie, setEditingMovie] = useState(null);
  const [editMovieTitle, setEditMovieTitle] = useState("");
  const [editMovieDescription, setEditMovieDescription] = useState("");
  const [editMovieDuration, setEditMovieDuration] = useState("");
  const [editMovieHeroImage, setEditMovieHeroImage] = useState(null);
  const [editMovieThumbFile, setEditMovieThumbFile] = useState(null);

  const [editMovieUseExternalUrl, setEditMovieUseExternalUrl] = useState(true);
  const [editMovieExternalVideoUrl, setEditMovieExternalVideoUrl] =
    useState("");
  const [editMovieVideoFile, setEditMovieVideoFile] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) =>
      setSession(s),
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;
    refreshShows();
    refreshMovies();
    refreshBanners();
    refreshCdnFiles();
    refreshBlogPosts();
  }, [session]);

  useEffect(() => {
    if (!session) return;

    if (!blogAuthor && session.user?.email) {
      setBlogAuthor(session.user.email);
    }

    if (!blogPublishedDate) {
      setBlogPublishedDate(new Date().toISOString().slice(0, 10));
    }
  }, [session, blogAuthor, blogPublishedDate]);

  useEffect(() => {
    if (selectedShowId) refreshSeasons(selectedShowId);
    else {
      setSeasons([]);
      setSelectedSeasonId("");
      setEpisodes([]);
    }
  }, [selectedShowId]);

  useEffect(() => {
    if (selectedSeasonId) refreshEpisodes(selectedSeasonId);
    else setEpisodes([]);
  }, [selectedSeasonId]);

  const selectedShow = useMemo(
    () => shows.find((s) => s.id === selectedShowId),
    [shows, selectedShowId],
  );

  const blogReadTimeLabel = useMemo(() => {
    const mins = Number(blogReadTimeMinutes) || 0;
    return mins > 0 ? `${mins} min read` : "";
  }, [blogReadTimeMinutes]);

  async function signIn() {
    setStatus("Signing in...");
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) setStatus(error.message);
    else setStatus("Signed in.");
  }

  async function signOut() {
    await supabase.auth.signOut();
    setStatus("Signed out.");
  }

  async function uploadToMedia(path, file) {
    const { error } = await supabase.storage.from("media").upload(path, file, {
      upsert: true,
      cacheControl: "3600",
      contentType: file.type,
    });
    if (error) throw error;
    return path;
  }

  // =========================
  // BLOGS
  // =========================
  function resetBlogForm() {
    setEditingBlogPostId(null);
    setBlogTitle("");
    setBlogSlug("");
    setBlogCategory("Product Update");
    setBlogAuthor(session?.user?.email || "");
    setBlogPublishedDate(new Date().toISOString().slice(0, 10));
    setBlogReadTimeMinutes(4);
    setBlogOverview("");
    setBlogSummary("");
    setBlogContent("");
    setBlogIsPublished(true);
  }

  function onBlogTitleChange(value) {
    setBlogTitle(value);
    if (!blogSlug || blogSlug === slugify(blogTitle)) {
      setBlogSlug(slugify(value));
    }
  }

  async function refreshBlogPosts() {
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .order("published_date", { ascending: false })
      .order("created_at", { ascending: false });

    if (error) {
      setStatus(error.message);
      setBlogPosts([]);
      return;
    }

    setBlogPosts(data || []);
  }

  function openEditBlogPost(post) {
    setEditingBlogPostId(post.id);
    setBlogTitle(post.title || "");
    setBlogSlug(post.slug || "");
    setBlogCategory(post.category || "Product Update");
    setBlogAuthor(post.author_name || "");
    setBlogPublishedDate(formatBlogDateForInput(post.published_date));
    setBlogReadTimeMinutes(post.read_time_minutes || 4);
    setBlogOverview(post.overview || "");
    setBlogSummary(post.summary || "");
    setBlogContent(post.content || "");
    setBlogIsPublished(!!post.is_published);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function saveBlogPost() {
    try {
      setStatus(
        editingBlogPostId ? "Saving blog post..." : "Creating blog post...",
      );

      if (!blogTitle.trim()) throw new Error("Blog title is required.");
      if (!blogSlug.trim()) throw new Error("Blog slug is required.");
      if (!blogAuthor.trim()) throw new Error("Blog author is required.");
      if (!blogPublishedDate) throw new Error("Published date is required.");
      if (!blogContent.trim()) throw new Error("Blog content is required.");

      const payload = {
        slug: slugify(blogSlug),
        category: blogCategory.trim() || "Product Update",
        title: blogTitle.trim(),
        author_name: blogAuthor.trim(),
        published_date: blogPublishedDate,
        read_time_minutes: Math.max(1, Number(blogReadTimeMinutes) || 1),
        overview: blogOverview.trim() || null,
        summary: blogSummary.trim() || null,
        content: blogContent.trim(),
        is_published: !!blogIsPublished,
        created_by: session?.user?.id || null,
      };

      if (editingBlogPostId) {
        const { error } = await supabase
          .from("blog_posts")
          .update(payload)
          .eq("id", editingBlogPostId);

        if (error) throw error;

        setStatus("Blog post updated.");
      } else {
        const { error } = await supabase.from("blog_posts").insert([payload]);

        if (error) throw error;

        setStatus("Blog post created.");
      }

      resetBlogForm();
      await refreshBlogPosts();
    } catch (e) {
      setStatus(e.message);
    }
  }

  async function deleteBlogPost(id) {
    if (!window.confirm("Delete blog post?")) return;

    try {
      setStatus("Deleting blog post...");
      const { error } = await supabase.from("blog_posts").delete().eq("id", id);
      if (error) throw error;

      if (editingBlogPostId === id) {
        resetBlogForm();
      }

      setStatus("Blog post deleted.");
      await refreshBlogPosts();
    } catch (e) {
      setStatus(e.message);
    }
  }

  // =========================
  // CDN
  // =========================
  async function refreshCdnFiles() {
    try {
      const res = await fetch("/api/cdn-list");
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.error || "Failed to list CDN files.");
      }

      setCdnFiles(json.files || []);
    } catch (e) {
      setStatus(e.message || "Failed to list CDN files.");
      setCdnFiles([]);
    }
  }

  async function uploadToCdn() {
    try {
      setStatus("Uploading to CDN...");

      if (!cdnFile) throw new Error("Pick a CDN file first.");
      const cleanedPath = normaliseCdnPath(cdnPath);
      if (!cleanedPath) throw new Error("Enter a CDN folder path.");

      const form = new FormData();
      form.append("file", cdnFile);
      form.append("path", cleanedPath);

      const res = await fetch("/api/cdn-upload", {
        method: "POST",
        body: form,
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.error || "Failed to upload to CDN.");
      }

      setCdnFile(null);
      setStatus(`CDN upload complete: ${json.url}`);
      await refreshCdnFiles();
    } catch (e) {
      setStatus(e.message || "Failed to upload to CDN.");
    }
  }

  async function deleteCdnFile(key) {
    if (!window.confirm("Delete CDN file?")) return;

    try {
      setStatus("Deleting CDN file...");

      const res = await fetch("/api/cdn-delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key }),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.error || "Failed to delete CDN file.");
      }

      setStatus("CDN file deleted.");
      await refreshCdnFiles();
    } catch (e) {
      setStatus(e.message || "Failed to delete CDN file.");
    }
  }

  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
      setStatus("Copied to clipboard.");
    } catch {
      setStatus("Failed to copy to clipboard.");
    }
  }

  // =========================
  // SERIES: REFRESH
  // =========================
  async function refreshShows() {
    const { data, error } = await supabase
      .from("shows")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) setStatus(error.message);
    setShows(data || []);
  }

  async function refreshSeasons(showId) {
    const { data, error } = await supabase
      .from("seasons")
      .select("*")
      .eq("show_id", showId)
      .order("season_number", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: true });

    if (error) setStatus(error.message);
    setSeasons(data || []);
  }

  async function refreshEpisodes(seasonId) {
    const { data, error } = await supabase
      .from("episodes")
      .select("*")
      .eq("season_id", seasonId)
      .order("episode_number", { ascending: true });

    if (error) setStatus(error.message);
    setEpisodes(data || []);
  }

  // =========================
  // MOVIES: REFRESH
  // =========================
  async function refreshMovies() {
    const { data, error } = await supabase
      .from("movies")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      setStatus(error.message);
      setMovies([]);
      return;
    }
    setMovies(data || []);
  }

  // =========================
  // MANAGE: BANNERS REFRESH
  // =========================
  async function refreshBanners() {
    const { data, error } = await supabase
      .from("banners")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      setStatus(error.message);
      setBanners([]);
      return;
    }
    setBanners(data || []);
  }

  // =========================
  // SERIES: CREATE
  // =========================
  async function createShow() {
    try {
      setStatus("Creating show...");

      let heroPath = null;
      if (showHeroImage) {
        const p = `shows/hero/${Date.now()}-${slugify(showTitle)}-${showHeroImage.name}`;
        heroPath = await uploadToMedia(p, showHeroImage);
      }

      const { error } = await supabase.from("shows").insert([
        {
          title: showTitle,
          description: showDescription,
          hero_image_path: heroPath,
        },
      ]);

      if (error) throw error;

      setShowTitle("");
      setShowDescription("");
      setShowHeroImage(null);

      setStatus("Show created.");
      await refreshShows();
    } catch (e) {
      setStatus(e.message);
    }
  }

  async function createSeason() {
    try {
      setStatus("Creating season...");
      if (!selectedShowId) throw new Error("Pick a show first.");

      const trimmed = seasonInput.trim();
      if (!trimmed) throw new Error("Season number or name required.");

      const parsed = Number(trimmed);
      const isNumber =
        trimmed !== "" && !Number.isNaN(parsed) && /^-?\d+$/.test(trimmed);

      const payload = isNumber
        ? {
            show_id: selectedShowId,
            season_number: parsed,
            season_label: null,
          }
        : {
            show_id: selectedShowId,
            season_number: null,
            season_label: trimmed,
          };

      const { error } = await supabase.from("seasons").insert([payload]);

      if (error) throw error;

      setSeasonInput("");
      setStatus("Season created.");
      await refreshSeasons(selectedShowId);
    } catch (e) {
      setStatus(e.message);
    }
  }

  async function createEpisode() {
    try {
      setStatus("Creating episode...");

      if (!selectedShowId) throw new Error("Pick a show first.");
      if (!selectedSeasonId) throw new Error("Pick a season first.");
      if (!episodeTitle) throw new Error("Episode title required.");

      const season = seasons.find((s) => s.id === selectedSeasonId);
      const sn = season?.season_number ?? 1;

      const base = `shows/${selectedShowId}/season-${sn}/ep-${episodeNumber}-${slugify(
        episodeTitle,
      )}`;

      let thumbPath = null;
      if (thumbFile) {
        thumbPath = await uploadToMedia(
          `${base}/thumb-${thumbFile.name}`,
          thumbFile,
        );
      }

      let videoUrl = null;
      let videoPath = null;

      if (useExternalUrl) {
        if (!externalVideoUrl) throw new Error("Paste a video URL.");
        videoUrl = toPlayableVideoUrl(externalVideoUrl);
      } else {
        if (!videoFile) throw new Error("Pick a video file to upload.");
        videoPath = await uploadToMedia(
          `${base}/video-${videoFile.name}`,
          videoFile,
        );
      }

      const { error } = await supabase.from("episodes").insert([
        {
          season_id: selectedSeasonId,
          episode_number: Number(episodeNumber),
          title: episodeTitle,
          description: episodeDescription || null,
          duration: duration || null,
          video_url: videoUrl,
          video_path: videoPath,
          thumbnail_path: thumbPath,
        },
      ]);

      if (error) throw error;

      setEpisodeTitle("");
      setEpisodeDescription("");
      setDuration("");
      setThumbFile(null);
      setVideoFile(null);
      setExternalVideoUrl("");

      setStatus("Episode created.");
      await refreshEpisodes(selectedSeasonId);
    } catch (e) {
      setStatus(e.message);
    }
  }

  // =========================
  // MOVIES: CREATE
  // =========================
  async function createMovie() {
    try {
      setStatus("Creating movie...");

      if (!movieTitle) throw new Error("Movie title required.");

      const base = `movies/${Date.now()}-${slugify(movieTitle)}`;

      let heroPath = null;
      if (movieHeroImage) {
        heroPath = await uploadToMedia(
          `${base}/hero-${movieHeroImage.name}`,
          movieHeroImage,
        );
      }

      let thumbPath = null;
      if (movieThumbFile) {
        thumbPath = await uploadToMedia(
          `${base}/thumb-${movieThumbFile.name}`,
          movieThumbFile,
        );
      }

      let videoUrl = null;
      let videoPath = null;

      if (movieUseExternalUrl) {
        if (!movieExternalVideoUrl) throw new Error("Paste a movie video URL.");
        videoUrl = toPlayableVideoUrl(movieExternalVideoUrl);
      } else {
        if (!movieVideoFile)
          throw new Error("Pick a movie video file to upload.");
        videoPath = await uploadToMedia(
          `${base}/video-${movieVideoFile.name}`,
          movieVideoFile,
        );
      }

      const { error } = await supabase.from("movies").insert([
        {
          title: movieTitle,
          description: movieDescription || null,
          duration: movieDuration || null,
          hero_image_path: heroPath,
          thumbnail_path: thumbPath,
          video_url: videoUrl,
          video_path: videoPath,
        },
      ]);

      if (error) throw error;

      setMovieTitle("");
      setMovieDescription("");
      setMovieDuration("");
      setMovieHeroImage(null);
      setMovieThumbFile(null);
      setMovieExternalVideoUrl("");
      setMovieVideoFile(null);

      setStatus("Movie created.");
      await refreshMovies();
    } catch (e) {
      setStatus(e.message);
    }
  }

  // =========================
  // MANAGE: BANNERS CREATE/UPDATE
  // =========================
  async function disableAllBanners() {
    const { error } = await supabase
      .from("banners")
      .update({ enabled: false })
      .eq("enabled", true);
    if (error) throw error;
  }

  async function createBanner() {
    try {
      setStatus("Creating banner...");
      if (!bannerMessage.trim()) throw new Error("Banner message required.");

      if (bannerEnabled) {
        await disableAllBanners();
      }

      const { error } = await supabase.from("banners").insert([
        {
          message: bannerMessage.trim(),
          enabled: !!bannerEnabled,
          variant: bannerVariant || "info",
          link_url: bannerLinkUrl ? bannerLinkUrl.trim() : null,
          link_text: bannerLinkText ? bannerLinkText.trim() : null,
        },
      ]);

      if (error) throw error;

      setBannerMessage("");
      setBannerEnabled(false);
      setBannerVariant("info");
      setBannerLinkUrl("");
      setBannerLinkText("");

      setStatus("Banner created.");
      await refreshBanners();
    } catch (e) {
      setStatus(e.message);
    }
  }

  function openEditBanner(b) {
    setEditingBanner(b);
    setEditBannerMessage(b.message || "");
    setEditBannerEnabled(!!b.enabled);
    setEditBannerVariant(b.variant || "info");
    setEditBannerLinkUrl(b.link_url || "");
    setEditBannerLinkText(b.link_text || "");
  }

  async function saveEditBanner() {
    try {
      if (!editingBanner) return;
      setStatus("Saving banner...");
      if (!editBannerMessage.trim())
        throw new Error("Banner message required.");

      if (editBannerEnabled) {
        await disableAllBanners();
      }

      const { error } = await supabase
        .from("banners")
        .update({
          message: editBannerMessage.trim(),
          enabled: !!editBannerEnabled,
          variant: editBannerVariant || "info",
          link_url: editBannerLinkUrl ? editBannerLinkUrl.trim() : null,
          link_text: editBannerLinkText ? editBannerLinkText.trim() : null,
        })
        .eq("id", editingBanner.id);

      if (error) throw error;

      setEditingBanner(null);
      setStatus("Banner updated.");
      await refreshBanners();
    } catch (e) {
      setStatus(e.message);
    }
  }

  async function deleteBanner(id) {
    if (!window.confirm("Delete banner?")) return;
    try {
      setStatus("Deleting banner...");
      const { error } = await supabase.from("banners").delete().eq("id", id);
      if (error) throw error;
      setStatus("Banner deleted.");
      await refreshBanners();
    } catch (e) {
      setStatus(e.message);
    }
  }

  async function toggleBannerEnabled(b) {
    try {
      setStatus("Updating banner...");
      if (!b) return;

      const next = !b.enabled;
      if (next) {
        await disableAllBanners();
      }

      const { error } = await supabase
        .from("banners")
        .update({ enabled: next })
        .eq("id", b.id);

      if (error) throw error;

      setStatus("Banner updated.");
      await refreshBanners();
    } catch (e) {
      setStatus(e.message);
    }
  }

  // =========================
  // SERIES: DELETE
  // =========================
  async function deleteShow(id) {
    if (!window.confirm("Delete show? This also deletes seasons & episodes."))
      return;
    try {
      setStatus("Deleting show...");
      const { error } = await supabase.from("shows").delete().eq("id", id);
      if (error) throw error;
      setStatus("Show deleted.");
      setSelectedShowId("");
      setSelectedSeasonId("");
      await refreshShows();
    } catch (e) {
      setStatus(e.message);
    }
  }

  async function deleteSeason(id) {
    if (!window.confirm("Delete season? This also deletes episodes.")) return;
    try {
      setStatus("Deleting season...");
      const { error } = await supabase.from("seasons").delete().eq("id", id);
      if (error) throw error;
      setStatus("Season deleted.");
      setSelectedSeasonId("");
      await refreshSeasons(selectedShowId);
    } catch (e) {
      setStatus(e.message);
    }
  }

  async function deleteEpisode(id) {
    if (!window.confirm("Delete episode?")) return;
    try {
      setStatus("Deleting episode...");
      const { error } = await supabase.from("episodes").delete().eq("id", id);
      if (error) throw error;
      setStatus("Episode deleted.");
      await refreshEpisodes(selectedSeasonId);
    } catch (e) {
      setStatus(e.message);
    }
  }

  // =========================
  // MOVIES: DELETE
  // =========================
  async function deleteMovie(id) {
    if (!window.confirm("Delete movie?")) return;
    try {
      setStatus("Deleting movie...");
      const { error } = await supabase.from("movies").delete().eq("id", id);
      if (error) throw error;
      setStatus("Movie deleted.");
      await refreshMovies();
    } catch (e) {
      setStatus(e.message);
    }
  }

  // =========================
  // SERIES: OPEN EDIT
  // =========================
  function openEditShow(s) {
    setEditingShow(s);
    setEditShowTitle(s.title || "");
    setEditShowDescription(s.description || "");
    setEditShowHeroImage(null);
  }

  function openEditSeason(s) {
    setEditingSeason(s);
    setEditSeasonInput(
      s.season_label?.trim() ||
        (s.season_number !== null && s.season_number !== undefined
          ? String(s.season_number)
          : ""),
    );
  }

  function openEditEpisode(e) {
    setEditingEpisode(e);
    setEditEpisodeNumber(e.episode_number || 1);
    setEditEpisodeTitle(e.title || "");
    setEditEpisodeDescription(e.description || "");
    setEditDuration(e.duration || "");
    setEditUseExternalUrl(!!e.video_url);
    setEditExternalVideoUrl(e.video_url || "");
    setEditVideoFile(null);
    setEditThumbFile(null);
  }

  // =========================
  // MOVIES: OPEN EDIT
  // =========================
  function openEditMovie(m) {
    setEditingMovie(m);
    setEditMovieTitle(m.title || "");
    setEditMovieDescription(m.description || "");
    setEditMovieDuration(m.duration || "");
    setEditMovieHeroImage(null);
    setEditMovieThumbFile(null);
    setEditMovieUseExternalUrl(!!m.video_url);
    setEditMovieExternalVideoUrl(m.video_url || "");
    setEditMovieVideoFile(null);
  }

  // =========================
  // SERIES: SAVE EDITS
  // =========================
  async function saveEditShow() {
    try {
      if (!editingShow) return;
      setStatus("Saving show...");

      let heroPath = editingShow.hero_image_path || null;
      if (editShowHeroImage) {
        const p = `shows/hero/${Date.now()}-${slugify(editShowTitle)}-${editShowHeroImage.name}`;
        heroPath = await uploadToMedia(p, editShowHeroImage);
      }

      const { error } = await supabase
        .from("shows")
        .update({
          title: editShowTitle,
          description: editShowDescription,
          hero_image_path: heroPath,
        })
        .eq("id", editingShow.id);

      if (error) throw error;

      setEditingShow(null);
      setStatus("Show updated.");
      await refreshShows();
    } catch (e) {
      setStatus(e.message);
    }
  }

  async function saveEditSeason() {
    try {
      if (!editingSeason) return;
      setStatus("Saving season...");

      const trimmed = editSeasonInput.trim();
      if (!trimmed) throw new Error("Season number or name required.");

      const parsed = Number(trimmed);
      const isNumber =
        trimmed !== "" && !Number.isNaN(parsed) && /^-?\d+$/.test(trimmed);

      const { error } = await supabase
        .from("seasons")
        .update({
          season_number: isNumber ? parsed : null,
          season_label: isNumber ? null : trimmed,
        })
        .eq("id", editingSeason.id);

      if (error) throw error;

      setEditingSeason(null);
      setStatus("Season updated.");
      await refreshSeasons(selectedShowId);
    } catch (e) {
      setStatus(e.message);
    }
  }

  async function saveEditEpisode() {
    try {
      if (!editingEpisode) return;
      setStatus("Saving episode...");

      const season = seasons.find((s) => s.id === selectedSeasonId);
      const sn = season?.season_number ?? 1;

      const base = `shows/${selectedShowId}/season-${sn}/ep-${editEpisodeNumber}-${slugify(
        editEpisodeTitle,
      )}`;

      let thumbPath = editingEpisode.thumbnail_path || null;
      if (editThumbFile) {
        thumbPath = await uploadToMedia(
          `${base}/thumb-${editThumbFile.name}`,
          editThumbFile,
        );
      }

      let videoUrl = editingEpisode.video_url || null;
      let videoPath = editingEpisode.video_path || null;

      if (editUseExternalUrl) {
        if (!editExternalVideoUrl) throw new Error("Paste a video URL.");
        videoUrl = toPlayableVideoUrl(editExternalVideoUrl);
        videoPath = null;
      } else {
        if (editVideoFile) {
          videoPath = await uploadToMedia(
            `${base}/video-${editVideoFile.name}`,
            editVideoFile,
          );
        }
        videoUrl = null;
      }

      const { error } = await supabase
        .from("episodes")
        .update({
          episode_number: Number(editEpisodeNumber),
          title: editEpisodeTitle,
          description: editEpisodeDescription || null,
          duration: editDuration || null,
          video_url: videoUrl,
          video_path: videoPath,
          thumbnail_path: thumbPath,
        })
        .eq("id", editingEpisode.id);

      if (error) throw error;

      setEditingEpisode(null);
      setStatus("Episode updated.");
      await refreshEpisodes(selectedSeasonId);
    } catch (e) {
      setStatus(e.message);
    }
  }

  // =========================
  // MOVIES: SAVE EDIT
  // =========================
  async function saveEditMovie() {
    try {
      if (!editingMovie) return;
      setStatus("Saving movie...");

      const base = `movies/${Date.now()}-${slugify(editMovieTitle)}`;

      let heroPath = editingMovie.hero_image_path || null;
      if (editMovieHeroImage) {
        heroPath = await uploadToMedia(
          `${base}/hero-${editMovieHeroImage.name}`,
          editMovieHeroImage,
        );
      }

      let thumbPath = editingMovie.thumbnail_path || null;
      if (editMovieThumbFile) {
        thumbPath = await uploadToMedia(
          `${base}/thumb-${editMovieThumbFile.name}`,
          editMovieThumbFile,
        );
      }

      let videoUrl = editingMovie.video_url || null;
      let videoPath = editingMovie.video_path || null;

      if (editMovieUseExternalUrl) {
        if (!editMovieExternalVideoUrl) throw new Error("Paste a video URL.");
        videoUrl = toPlayableVideoUrl(editMovieExternalVideoUrl);
        videoPath = null;
      } else {
        if (editMovieVideoFile) {
          videoPath = await uploadToMedia(
            `${base}/video-${editMovieVideoFile.name}`,
            editMovieVideoFile,
          );
        }
        videoUrl = null;
      }

      const { error } = await supabase
        .from("movies")
        .update({
          title: editMovieTitle,
          description: editMovieDescription || null,
          duration: editMovieDuration || null,
          hero_image_path: heroPath,
          thumbnail_path: thumbPath,
          video_url: videoUrl,
          video_path: videoPath,
        })
        .eq("id", editingMovie.id);

      if (error) throw error;

      setEditingMovie(null);
      setStatus("Movie updated.");
      await refreshMovies();
    } catch (e) {
      setStatus(e.message);
    }
  }

  // =========================
  // AUTH UI
  // =========================
  if (!session) {
    return (
      <div className="min-h-screen bg-[#0b0c0c] text-white p-8">
        <h1 className="text-3xl font-bold mb-6">Admin Login</h1>
        <div className="max-w-md space-y-3">
          <input
            className="w-full p-3 rounded text-black"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            className="w-full p-3 rounded text-black"
            placeholder="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            onClick={signIn}
            className="bg-white text-black px-6 py-3 rounded font-semibold"
          >
            Sign in
          </button>
          {status ? <p className="text-gray-300">{status}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0c0c] text-white p-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold">Admin Panel</h1>
        <button
          onClick={signOut}
          className="bg-white text-black px-4 py-2 rounded"
        >
          Sign out
        </button>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={() => setMode("series")}
          className={`px-4 py-2 rounded font-semibold ${
            mode === "series" ? "bg-white text-black" : "bg-white/10 text-white"
          }`}
        >
          Series
        </button>
        <button
          onClick={() => setMode("movies")}
          className={`px-4 py-2 rounded font-semibold ${
            mode === "movies" ? "bg-white text-black" : "bg-white/10 text-white"
          }`}
        >
          Movies
        </button>
        <button
          onClick={() => setMode("blogs")}
          className={`px-4 py-2 rounded font-semibold ${
            mode === "blogs" ? "bg-white text-black" : "bg-white/10 text-white"
          }`}
        >
          Blogs
        </button>
        <button
          onClick={() => setMode("manage")}
          className={`px-4 py-2 rounded font-semibold ${
            mode === "manage" ? "bg-white text-black" : "bg-white/10 text-white"
          }`}
        >
          Manage
        </button>
        <button
          onClick={() => setMode("cdn")}
          className={`px-4 py-2 rounded font-semibold ${
            mode === "cdn" ? "bg-white text-black" : "bg-white/10 text-white"
          }`}
        >
          CDN
        </button>
      </div>

      {mode === "series" && selectedShowId ? (
        <p className="text-sm text-gray-400 mb-4">
          Selected show:{" "}
          <span className="text-gray-200 font-semibold">
            {selectedShow?.title || "Unknown"}
          </span>
        </p>
      ) : null}

      {status ? (
        <div className="mb-6 p-3 rounded bg-white/10 border border-white/10">
          {status}
        </div>
      ) : null}

      {mode === "blogs" ? (
        <>
          <div className="mb-10 p-6 rounded bg-white/5 border border-white/10">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-xl font-semibold">
                  {editingBlogPostId ? "Edit Blog Post" : "Create Blog Post"}
                </h2>
                <p className="text-sm text-gray-400 mt-2">
                  Create and publish blog posts for the blog area. The slug
                  becomes the URL, such as{" "}
                  <span className="text-gray-200">/blogs/updated-player</span>.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={refreshBlogPosts}
                  className="px-3 py-2 rounded bg-white/10 font-semibold"
                >
                  Refresh
                </button>
                <button
                  onClick={resetBlogForm}
                  className="px-3 py-2 rounded bg-white text-black font-semibold"
                >
                  New Post
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-3 max-w-4xl">
              <input
                className="p-3 rounded text-black"
                placeholder="Blog title"
                value={blogTitle}
                onChange={(e) => onBlogTitleChange(e.target.value)}
              />

              <input
                className="p-3 rounded text-black"
                placeholder="Slug / URL (e.g. updated-player)"
                value={blogSlug}
                onChange={(e) => setBlogSlug(slugify(e.target.value))}
              />

              <div className="grid md:grid-cols-2 gap-3">
                <input
                  className="p-3 rounded text-black"
                  placeholder="Category"
                  value={blogCategory}
                  onChange={(e) => setBlogCategory(e.target.value)}
                />

                <input
                  className="p-3 rounded text-black"
                  placeholder="Author"
                  value={blogAuthor}
                  onChange={(e) => setBlogAuthor(e.target.value)}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <input
                  className="p-3 rounded text-black"
                  type="date"
                  value={blogPublishedDate}
                  onChange={(e) => setBlogPublishedDate(e.target.value)}
                />

                <input
                  className="p-3 rounded text-black"
                  type="number"
                  min="1"
                  placeholder="Read time in mins"
                  value={blogReadTimeMinutes}
                  onChange={(e) => setBlogReadTimeMinutes(e.target.value)}
                />
              </div>

              <input
                className="p-3 rounded text-black"
                placeholder="Overview"
                value={blogOverview}
                onChange={(e) => setBlogOverview(e.target.value)}
              />

              <input
                className="p-3 rounded text-black"
                placeholder="Summary"
                value={blogSummary}
                onChange={(e) => setBlogSummary(e.target.value)}
              />

              <textarea
                className="p-3 rounded text-black min-h-[280px]"
                placeholder="Full blog content. Use blank lines between paragraphs."
                value={blogContent}
                onChange={(e) => setBlogContent(e.target.value)}
              />

              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-200">
                  <input
                    type="checkbox"
                    checked={blogIsPublished}
                    onChange={(e) => setBlogIsPublished(e.target.checked)}
                  />
                  Publish immediately
                </label>

                <span className="text-sm text-gray-400">
                  {blogReadTimeLabel}
                </span>
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={saveBlogPost}
                  className="bg-white text-black px-5 py-3 rounded font-semibold"
                >
                  {editingBlogPostId ? "Save Blog Post" : "Create Blog Post"}
                </button>

                {editingBlogPostId ? (
                  <button
                    onClick={resetBlogForm}
                    className="bg-white/10 px-5 py-3 rounded font-semibold"
                  >
                    Cancel Edit
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="p-6 rounded bg-white/5 border border-white/10">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-xl font-semibold">Existing Blog Posts</h2>
              <button
                onClick={refreshBlogPosts}
                className="px-3 py-2 rounded bg-white/10 font-semibold"
              >
                Refresh
              </button>
            </div>

            <div className="mt-4 grid gap-3">
              {blogPosts.map((post) => (
                <div
                  key={post.id}
                  className="p-3 rounded border border-white/10 bg-white/5"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs px-2 py-1 rounded bg-white/10">
                          {post.category || "Product Update"}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            post.is_published
                              ? "bg-green-500/20 text-green-100"
                              : "bg-yellow-500/20 text-yellow-100"
                          }`}
                        >
                          {post.is_published ? "Published" : "Draft"}
                        </span>
                      </div>

                      <div className="font-semibold break-words">
                        {post.title}
                      </div>

                      <div className="text-xs text-gray-400 mt-1 break-words">
                        /blogs/{post.slug}
                      </div>

                      <div className="text-xs text-gray-500 mt-1">
                        {post.author_name} • {post.read_time_minutes} min read •{" "}
                        {formatBlogDateForInput(post.published_date)}
                      </div>

                      {post.overview || post.summary ? (
                        <div className="text-sm text-gray-300 mt-2 break-words">
                          {post.overview || post.summary}
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openEditBlogPost(post)}
                        className="px-3 py-2 rounded bg-white text-black font-semibold"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() =>
                          window.open(`/blogs/${post.slug}`, "_blank")
                        }
                        className="px-3 py-2 rounded bg-white/10 font-semibold"
                      >
                        View
                      </button>
                      <button
                        onClick={() => deleteBlogPost(post.id)}
                        className="px-3 py-2 rounded bg-red-600 text-white font-semibold"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {!blogPosts.length ? (
                <p className="text-sm text-gray-400">No blog posts yet.</p>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      {mode === "cdn" ? (
        <div className="mb-10 p-6 rounded bg-white/5 border border-white/10">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-xl font-semibold">CDN</h2>
              <p className="text-sm text-gray-400 mt-2">
                Upload files directly to your Cloudflare R2-backed CDN bucket.
              </p>
            </div>

            <button
              onClick={refreshCdnFiles}
              className="px-3 py-2 rounded bg-white/10 font-semibold"
            >
              Refresh
            </button>
          </div>

          <div className="mt-6 grid gap-3 max-w-3xl">
            <input
              className="p-3 rounded text-black"
              placeholder="CDN folder path (e.g. TGGTA/season3)"
              value={cdnPath}
              onChange={(e) => setCdnPath(e.target.value)}
            />

            <label className="text-sm text-gray-300">
              File
              <input
                className="block mt-2"
                type="file"
                onChange={(e) => setCdnFile(e.target.files?.[0] || null)}
              />
            </label>

            <button
              onClick={uploadToCdn}
              className="bg-white text-black px-5 py-3 rounded font-semibold w-fit"
            >
              Upload to CDN
            </button>
          </div>

          <div className="mt-8 border-t border-white/10 pt-6">
            <h3 className="text-lg font-semibold mb-3">CDN Files</h3>

            <div className="grid gap-3">
              {cdnFiles.map((file) => (
                <div
                  key={file.key}
                  className="p-3 rounded border border-white/10 bg-white/5"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="font-semibold break-words">
                        {file.key}
                      </div>
                      <div className="text-xs text-gray-400 mt-1 break-words">
                        {file.url}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {file.sizeLabel}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => copyText(file.url)}
                        className="px-3 py-2 rounded bg-white text-black font-semibold"
                      >
                        Copy URL
                      </button>
                      <button
                        onClick={() => deleteCdnFile(file.key)}
                        className="px-3 py-2 rounded bg-red-600 text-white font-semibold"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {!cdnFiles.length ? (
                <p className="text-sm text-gray-400">No CDN files found yet.</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {mode === "manage" ? (
        <>
          <div className="mb-10 p-6 rounded bg-white/5 border border-white/10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Manage</h2>
              <button
                onClick={refreshBanners}
                className="px-3 py-2 rounded bg-white/10 font-semibold"
              >
                Refresh
              </button>
            </div>

            <p className="text-sm text-gray-400 mt-2">
              Banners show on the Home page under the header. If you enable a
              banner, this panel will auto-disable any other enabled banner so
              only one is live.
            </p>

            <div className="mt-4 grid gap-3">
              {banners.map((b) => (
                <div
                  key={b.id}
                  className={`p-3 rounded border ${
                    b.enabled
                      ? "border-white/40 bg-white/10"
                      : "border-white/10 bg-white/5"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            b.variant === "warning"
                              ? "bg-yellow-500/20"
                              : b.variant === "success"
                                ? "bg-green-500/20"
                                : b.variant === "danger"
                                  ? "bg-red-500/20"
                                  : "bg-white/10"
                          }`}
                        >
                          {b.variant || "info"}
                        </span>
                        {b.enabled ? (
                          <span className="text-xs px-2 py-1 rounded bg-white text-black font-semibold">
                            ENABLED
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-white/10 text-gray-200">
                            disabled
                          </span>
                        )}
                      </div>

                      <div className="text-sm text-gray-100 break-words">
                        {b.message}
                      </div>

                      {b.link_url || b.link_text ? (
                        <div className="text-xs text-gray-400 mt-1 break-words">
                          Link: {b.link_text || "Learn more"} — {b.link_url}
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end">
                      <button
                        onClick={() => toggleBannerEnabled(b)}
                        className="px-3 py-2 rounded bg-white text-black font-semibold"
                      >
                        {b.enabled ? "Disable" : "Enable"}
                      </button>
                      <button
                        onClick={() => openEditBanner(b)}
                        className="px-3 py-2 rounded bg-white/10 font-semibold"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteBanner(b.id)}
                        className="px-3 py-2 rounded bg-red-600 text-white font-semibold"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {!banners.length ? (
                <p className="text-sm text-gray-400">
                  No banners yet. Create one below.
                </p>
              ) : null}
            </div>

            <div className="mt-6 border-t border-white/10 pt-6">
              <h3 className="text-lg font-semibold mb-3">Create Banner</h3>

              <div className="grid gap-3 max-w-3xl">
                <textarea
                  className="p-3 rounded text-black"
                  placeholder='Banner text (e.g. "This show is leaving soon...")'
                  value={bannerMessage}
                  onChange={(e) => setBannerMessage(e.target.value)}
                />

                <div className="grid md:grid-cols-3 gap-3">
                  <select
                    className="p-3 rounded text-black"
                    value={bannerVariant}
                    onChange={(e) => setBannerVariant(e.target.value)}
                  >
                    <option value="info">info</option>
                    <option value="warning">warning</option>
                    <option value="success">success</option>
                    <option value="danger">danger</option>
                  </select>

                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      checked={bannerEnabled}
                      onChange={(e) => setBannerEnabled(e.target.checked)}
                    />
                    Enable immediately
                  </label>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <input
                    className="p-3 rounded text-black"
                    placeholder="Optional link URL"
                    value={bannerLinkUrl}
                    onChange={(e) => setBannerLinkUrl(e.target.value)}
                  />
                  <input
                    className="p-3 rounded text-black"
                    placeholder='Optional link text (e.g. "Read more")'
                    value={bannerLinkText}
                    onChange={(e) => setBannerLinkText(e.target.value)}
                  />
                </div>

                <button
                  onClick={createBanner}
                  className="bg-white text-black px-5 py-3 rounded font-semibold w-fit"
                >
                  Add Banner
                </button>
              </div>
            </div>
          </div>

          {editingBanner ? (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4">
              <div className="w-full max-w-3xl bg-[#0b0c0c] border border-white/10 rounded p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">Edit Banner</h3>
                  <button
                    onClick={() => setEditingBanner(null)}
                    className="px-3 py-2 rounded bg-white/10"
                  >
                    Close
                  </button>
                </div>

                <div className="grid gap-3">
                  <textarea
                    className="p-3 rounded text-black"
                    value={editBannerMessage}
                    onChange={(e) => setEditBannerMessage(e.target.value)}
                    placeholder="Banner text"
                  />

                  <div className="grid md:grid-cols-3 gap-3">
                    <select
                      className="p-3 rounded text-black"
                      value={editBannerVariant}
                      onChange={(e) => setEditBannerVariant(e.target.value)}
                    >
                      <option value="info">info</option>
                      <option value="warning">warning</option>
                      <option value="success">success</option>
                      <option value="danger">danger</option>
                    </select>

                    <label className="flex items-center gap-2 text-sm text-gray-200">
                      <input
                        type="checkbox"
                        checked={editBannerEnabled}
                        onChange={(e) => setEditBannerEnabled(e.target.checked)}
                      />
                      Enabled
                    </label>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <input
                      className="p-3 rounded text-black"
                      placeholder="Optional link URL"
                      value={editBannerLinkUrl}
                      onChange={(e) => setEditBannerLinkUrl(e.target.value)}
                    />
                    <input
                      className="p-3 rounded text-black"
                      placeholder="Optional link text"
                      value={editBannerLinkText}
                      onChange={(e) => setEditBannerLinkText(e.target.value)}
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={saveEditBanner}
                      className="bg-white text-black px-5 py-3 rounded font-semibold"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingBanner(null)}
                      className="bg-white/10 px-5 py-3 rounded font-semibold"
                    >
                      Cancel
                    </button>
                  </div>

                  <p className="text-xs text-gray-400">
                    If you enable this banner, any other enabled banner will be
                    disabled automatically.
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {mode === "series" ? (
        <>
          <div className="mb-10 p-6 rounded bg-white/5 border border-white/10">
            <h2 className="text-xl font-semibold">Shows</h2>

            <div className="mt-4 grid gap-3">
              {shows.map((s) => (
                <div
                  key={s.id}
                  className={`flex items-center justify-between p-3 rounded border ${
                    selectedShowId === s.id
                      ? "border-white/40 bg-white/10"
                      : "border-white/10 bg-white/5"
                  }`}
                >
                  <button
                    className="text-left flex-1"
                    onClick={() => {
                      setSelectedShowId(s.id);
                      setSelectedSeasonId("");
                    }}
                  >
                    <div className="font-semibold">{s.title}</div>
                    <div className="text-sm text-gray-400 line-clamp-1">
                      {s.description}
                    </div>
                  </button>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditShow(s)}
                      className="px-3 py-2 rounded bg-white text-black font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteShow(s.id)}
                      className="px-3 py-2 rounded bg-red-600 text-white font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 border-t border-white/10 pt-6">
              <h3 className="text-lg font-semibold mb-3">Create Show</h3>
              <div className="grid gap-3 max-w-2xl">
                <input
                  className="p-3 rounded text-black"
                  placeholder="Title"
                  value={showTitle}
                  onChange={(e) => setShowTitle(e.target.value)}
                />
                <textarea
                  className="p-3 rounded text-black"
                  placeholder="Description"
                  value={showDescription}
                  onChange={(e) => setShowDescription(e.target.value)}
                />
                <label className="text-sm text-gray-300">
                  Hero image (optional)
                  <input
                    className="block mt-2"
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setShowHeroImage(e.target.files?.[0] || null)
                    }
                  />
                </label>

                <button
                  onClick={createShow}
                  className="bg-white text-black px-5 py-3 rounded font-semibold w-fit"
                >
                  Add Show
                </button>
              </div>
            </div>
          </div>

          <div className="mb-10 p-6 rounded bg-white/5 border border-white/10">
            <h2 className="text-xl font-semibold">Seasons</h2>
            {!selectedShowId ? (
              <p className="text-gray-400 mt-3">
                Select a show to manage seasons.
              </p>
            ) : (
              <>
                <div className="mt-4 grid gap-3">
                  {seasons.map((s) => (
                    <div
                      key={s.id}
                      className={`flex items-center justify-between p-3 rounded border ${
                        selectedSeasonId === s.id
                          ? "border-white/40 bg-white/10"
                          : "border-white/10 bg-white/5"
                      }`}
                    >
                      <button
                        className="text-left flex-1"
                        onClick={() => setSelectedSeasonId(s.id)}
                      >
                        <div className="font-semibold">
                          {formatSeasonDisplay(s)}
                        </div>
                      </button>

                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditSeason(s)}
                          className="px-3 py-2 rounded bg-white text-black font-semibold"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteSeason(s.id)}
                          className="px-3 py-2 rounded bg-red-600 text-white font-semibold"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 border-t border-white/10 pt-6">
                  <h3 className="text-lg font-semibold mb-3">Create Season</h3>
                  <div className="grid gap-3 max-w-md">
                    <input
                      className="p-3 rounded text-black"
                      value={seasonInput}
                      onChange={(e) => setSeasonInput(e.target.value)}
                      placeholder="Season number or name (e.g. 1 or Extras)"
                    />
                    <button
                      onClick={createSeason}
                      className="bg-white text-black px-5 py-3 rounded font-semibold w-fit"
                    >
                      Add Season
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="p-6 rounded bg-white/5 border border-white/10">
            <h2 className="text-xl font-semibold">Episodes</h2>
            {!selectedSeasonId ? (
              <p className="text-gray-400 mt-3">
                Select a season to manage episodes.
              </p>
            ) : (
              <>
                <div className="mt-4 grid gap-3">
                  {episodes.map((e) => (
                    <div
                      key={e.id}
                      className="flex items-center justify-between p-3 rounded border border-white/10 bg-white/5"
                    >
                      <div className="flex-1">
                        <div className="font-semibold">
                          {e.episode_number}. {e.title}
                        </div>
                        <div className="text-sm text-gray-400 line-clamp-1">
                          {e.duration ? `${e.duration} • ` : ""}
                          {e.description || ""}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditEpisode(e)}
                          className="px-3 py-2 rounded bg-white text-black font-semibold"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteEpisode(e.id)}
                          className="px-3 py-2 rounded bg-red-600 text-white font-semibold"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 border-t border-white/10 pt-6">
                  <h3 className="text-lg font-semibold mb-3">Create Episode</h3>

                  <div className="grid gap-3 max-w-3xl">
                    <div className="grid md:grid-cols-3 gap-3">
                      <input
                        className="p-3 rounded text-black"
                        type="number"
                        min="1"
                        value={episodeNumber}
                        onChange={(e) => setEpisodeNumber(e.target.value)}
                        placeholder="Episode number"
                      />
                      <input
                        className="p-3 rounded text-black md:col-span-2"
                        value={episodeTitle}
                        onChange={(e) => setEpisodeTitle(e.target.value)}
                        placeholder="Episode title"
                      />
                    </div>

                    <textarea
                      className="p-3 rounded text-black"
                      value={episodeDescription}
                      onChange={(e) => setEpisodeDescription(e.target.value)}
                      placeholder="Episode description (optional)"
                    />

                    <input
                      className="p-3 rounded text-black"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      placeholder='Duration (e.g. "45m")'
                    />

                    <label className="text-sm text-gray-300">
                      Thumbnail (optional)
                      <input
                        className="block mt-2"
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          setThumbFile(e.target.files?.[0] || null)
                        }
                      />
                    </label>

                    <div className="flex flex-wrap items-center gap-4 mt-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={useExternalUrl}
                          onChange={() => setUseExternalUrl(true)}
                        />
                        <span className="text-sm text-gray-200">
                          Use video URL (Direct MP4 / Dropbox / Google Drive)
                        </span>
                      </label>

                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={!useExternalUrl}
                          onChange={() => setUseExternalUrl(false)}
                        />
                        <span className="text-sm text-gray-200">
                          Upload video file (Supabase)
                        </span>
                      </label>
                    </div>

                    {useExternalUrl ? (
                      <input
                        className="p-3 rounded text-black"
                        value={externalVideoUrl}
                        onChange={(e) => setExternalVideoUrl(e.target.value)}
                        placeholder="Paste a direct MP4 URL, Dropbox share link, or Google Drive share link"
                      />
                    ) : (
                      <label className="text-sm text-gray-300">
                        Video file (required)
                        <input
                          className="block mt-2"
                          type="file"
                          accept="video/*"
                          onChange={(e) =>
                            setVideoFile(e.target.files?.[0] || null)
                          }
                        />
                        <div className="text-xs text-gray-400 mt-1">
                          Supabase free plan may limit upload size. Use external
                          URL for bigger files.
                        </div>
                      </label>
                    )}

                    <button
                      onClick={createEpisode}
                      className="bg-white text-black px-5 py-3 rounded font-semibold w-fit"
                    >
                      Add Episode
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {editingShow ? (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4">
              <div className="w-full max-w-2xl bg-[#0b0c0c] border border-white/10 rounded p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">Edit Show</h3>
                  <button
                    onClick={() => setEditingShow(null)}
                    className="px-3 py-2 rounded bg-white/10"
                  >
                    Close
                  </button>
                </div>

                <div className="grid gap-3">
                  <input
                    className="p-3 rounded text-black"
                    value={editShowTitle}
                    onChange={(e) => setEditShowTitle(e.target.value)}
                    placeholder="Title"
                  />
                  <textarea
                    className="p-3 rounded text-black"
                    value={editShowDescription}
                    onChange={(e) => setEditShowDescription(e.target.value)}
                    placeholder="Description"
                  />
                  <label className="text-sm text-gray-300">
                    Replace hero image (optional)
                    <input
                      className="block mt-2"
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setEditShowHeroImage(e.target.files?.[0] || null)
                      }
                    />
                  </label>

                  <div className="flex gap-2">
                    <button
                      onClick={saveEditShow}
                      className="bg-white text-black px-5 py-3 rounded font-semibold"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingShow(null)}
                      className="bg-white/10 px-5 py-3 rounded font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {editingSeason ? (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4">
              <div className="w-full max-w-md bg-[#0b0c0c] border border-white/10 rounded p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">Edit Season</h3>
                  <button
                    onClick={() => setEditingSeason(null)}
                    className="px-3 py-2 rounded bg-white/10"
                  >
                    Close
                  </button>
                </div>

                <div className="grid gap-3">
                  <input
                    className="p-3 rounded text-black"
                    value={editSeasonInput}
                    onChange={(e) => setEditSeasonInput(e.target.value)}
                    placeholder="Season number or name"
                  />

                  <div className="flex gap-2">
                    <button
                      onClick={saveEditSeason}
                      className="bg-white text-black px-5 py-3 rounded font-semibold"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingSeason(null)}
                      className="bg-white/10 px-5 py-3 rounded font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {editingEpisode ? (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4">
              <div className="w-full max-w-3xl bg-[#0b0c0c] border border-white/10 rounded p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">Edit Episode</h3>
                  <button
                    onClick={() => setEditingEpisode(null)}
                    className="px-3 py-2 rounded bg-white/10"
                  >
                    Close
                  </button>
                </div>

                <div className="grid gap-3">
                  <div className="grid md:grid-cols-3 gap-3">
                    <input
                      className="p-3 rounded text-black"
                      type="number"
                      min="1"
                      value={editEpisodeNumber}
                      onChange={(e) => setEditEpisodeNumber(e.target.value)}
                      placeholder="Episode number"
                    />
                    <input
                      className="p-3 rounded text-black md:col-span-2"
                      value={editEpisodeTitle}
                      onChange={(e) => setEditEpisodeTitle(e.target.value)}
                      placeholder="Episode title"
                    />
                  </div>

                  <textarea
                    className="p-3 rounded text-black"
                    value={editEpisodeDescription}
                    onChange={(e) => setEditEpisodeDescription(e.target.value)}
                    placeholder="Episode description"
                  />

                  <input
                    className="p-3 rounded text-black"
                    value={editDuration}
                    onChange={(e) => setEditDuration(e.target.value)}
                    placeholder='Duration (e.g. "45m")'
                  />

                  <label className="text-sm text-gray-300">
                    Replace thumbnail (optional)
                    <input
                      className="block mt-2"
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setEditThumbFile(e.target.files?.[0] || null)
                      }
                    />
                  </label>

                  <div className="flex flex-wrap items-center gap-4 mt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={editUseExternalUrl}
                        onChange={() => setEditUseExternalUrl(true)}
                      />
                      <span className="text-sm text-gray-200">
                        Use video URL (Direct MP4 / Dropbox / Google Drive)
                      </span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={!editUseExternalUrl}
                        onChange={() => setEditUseExternalUrl(false)}
                      />
                      <span className="text-sm text-gray-200">
                        Upload video file (Supabase)
                      </span>
                    </label>
                  </div>

                  {editUseExternalUrl ? (
                    <input
                      className="p-3 rounded text-black"
                      value={editExternalVideoUrl}
                      onChange={(e) => setEditExternalVideoUrl(e.target.value)}
                      placeholder="Paste a direct MP4 URL, Dropbox share link, or Google Drive share link"
                    />
                  ) : (
                    <label className="text-sm text-gray-300">
                      Replace video file (optional)
                      <input
                        className="block mt-2"
                        type="file"
                        accept="video/*"
                        onChange={(e) =>
                          setEditVideoFile(e.target.files?.[0] || null)
                        }
                      />
                      <div className="text-xs text-gray-400 mt-1">
                        If you don't pick a file, the existing uploaded
                        video_path will stay.
                      </div>
                    </label>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={saveEditEpisode}
                      className="bg-white text-black px-5 py-3 rounded font-semibold"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingEpisode(null)}
                      className="bg-white/10 px-5 py-3 rounded font-semibold"
                    >
                      Cancel
                    </button>
                  </div>

                  <p className="text-xs text-gray-400">
                    Tip: If you switch to external URL, the stored video_path
                    will be cleared. If you switch to Storage, video_url will be
                    cleared.
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {mode === "movies" ? (
        <>
          <div className="mb-10 p-6 rounded bg-white/5 border border-white/10">
            <h2 className="text-xl font-semibold">Movies</h2>

            <div className="mt-4 grid gap-3">
              {movies.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between p-3 rounded border border-white/10 bg-white/5"
                >
                  <button
                    className="text-left flex-1"
                    onClick={() => openEditMovie(m)}
                  >
                    <div className="font-semibold">{m.title}</div>
                    <div className="text-sm text-gray-400 line-clamp-1">
                      {m.duration ? `${m.duration} • ` : ""}
                      {m.description || ""}
                    </div>
                  </button>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditMovie(m)}
                      className="px-3 py-2 rounded bg-white text-black font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteMovie(m.id)}
                      className="px-3 py-2 rounded bg-red-600 text-white font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 border-t border-white/10 pt-6">
              <h3 className="text-lg font-semibold mb-3">Create Movie</h3>

              <div className="grid gap-3 max-w-3xl">
                <input
                  className="p-3 rounded text-black"
                  placeholder="Movie title"
                  value={movieTitle}
                  onChange={(e) => setMovieTitle(e.target.value)}
                />

                <textarea
                  className="p-3 rounded text-black"
                  placeholder="Movie description (optional)"
                  value={movieDescription}
                  onChange={(e) => setMovieDescription(e.target.value)}
                />

                <input
                  className="p-3 rounded text-black"
                  placeholder='Duration (e.g. "1h 45m") (optional)'
                  value={movieDuration}
                  onChange={(e) => setMovieDuration(e.target.value)}
                />

                <label className="text-sm text-gray-300">
                  Hero image (optional)
                  <input
                    className="block mt-2"
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setMovieHeroImage(e.target.files?.[0] || null)
                    }
                  />
                </label>

                <label className="text-sm text-gray-300">
                  Thumbnail/poster (optional)
                  <input
                    className="block mt-2"
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setMovieThumbFile(e.target.files?.[0] || null)
                    }
                  />
                </label>

                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={movieUseExternalUrl}
                      onChange={() => setMovieUseExternalUrl(true)}
                    />
                    <span className="text-sm text-gray-200">
                      Use video URL (Direct MP4 / Dropbox / Google Drive)
                    </span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!movieUseExternalUrl}
                      onChange={() => setMovieUseExternalUrl(false)}
                    />
                    <span className="text-sm text-gray-200">
                      Upload video file (Supabase)
                    </span>
                  </label>
                </div>

                {movieUseExternalUrl ? (
                  <input
                    className="p-3 rounded text-black"
                    value={movieExternalVideoUrl}
                    onChange={(e) => setMovieExternalVideoUrl(e.target.value)}
                    placeholder="Paste a direct MP4 URL, Dropbox share link, or Google Drive share link"
                  />
                ) : (
                  <label className="text-sm text-gray-300">
                    Movie video file (required)
                    <input
                      className="block mt-2"
                      type="file"
                      accept="video/*"
                      onChange={(e) =>
                        setMovieVideoFile(e.target.files?.[0] || null)
                      }
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      Supabase free plan may limit upload size. Use external URL
                      for bigger files.
                    </div>
                  </label>
                )}

                <button
                  onClick={createMovie}
                  className="bg-white text-black px-5 py-3 rounded font-semibold w-fit"
                >
                  Add Movie
                </button>
              </div>
            </div>
          </div>

          {editingMovie ? (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4">
              <div className="w-full max-w-3xl bg-[#0b0c0c] border border-white/10 rounded p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">Edit Movie</h3>
                  <button
                    onClick={() => setEditingMovie(null)}
                    className="px-3 py-2 rounded bg-white/10"
                  >
                    Close
                  </button>
                </div>

                <div className="grid gap-3">
                  <input
                    className="p-3 rounded text-black"
                    value={editMovieTitle}
                    onChange={(e) => setEditMovieTitle(e.target.value)}
                    placeholder="Movie title"
                  />

                  <textarea
                    className="p-3 rounded text-black"
                    value={editMovieDescription}
                    onChange={(e) => setEditMovieDescription(e.target.value)}
                    placeholder="Movie description"
                  />

                  <input
                    className="p-3 rounded text-black"
                    value={editMovieDuration}
                    onChange={(e) => setEditMovieDuration(e.target.value)}
                    placeholder='Duration (e.g. "1h 45m")'
                  />

                  <label className="text-sm text-gray-300">
                    Replace hero image (optional)
                    <input
                      className="block mt-2"
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setEditMovieHeroImage(e.target.files?.[0] || null)
                      }
                    />
                  </label>

                  <label className="text-sm text-gray-300">
                    Replace thumbnail/poster (optional)
                    <input
                      className="block mt-2"
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setEditMovieThumbFile(e.target.files?.[0] || null)
                      }
                    />
                  </label>

                  <div className="flex flex-wrap items-center gap-4 mt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={editMovieUseExternalUrl}
                        onChange={() => setEditMovieUseExternalUrl(true)}
                      />
                      <span className="text-sm text-gray-200">
                        Use video URL (Direct MP4 / Dropbox / Google Drive)
                      </span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={!editMovieUseExternalUrl}
                        onChange={() => setEditMovieUseExternalUrl(false)}
                      />
                      <span className="text-sm text-gray-200">
                        Upload video file (Supabase)
                      </span>
                    </label>
                  </div>

                  {editMovieUseExternalUrl ? (
                    <input
                      className="p-3 rounded text-black"
                      value={editMovieExternalVideoUrl}
                      onChange={(e) =>
                        setEditMovieExternalVideoUrl(e.target.value)
                      }
                      placeholder="Paste a direct MP4 URL, Dropbox share link, or Google Drive share link"
                    />
                  ) : (
                    <label className="text-sm text-gray-300">
                      Replace movie video file (optional)
                      <input
                        className="block mt-2"
                        type="file"
                        accept="video/*"
                        onChange={(e) =>
                          setEditMovieVideoFile(e.target.files?.[0] || null)
                        }
                      />
                      <div className="text-xs text-gray-400 mt-1">
                        If you don't pick a file, the existing uploaded
                        video_path will stay.
                      </div>
                    </label>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={saveEditMovie}
                      className="bg-white text-black px-5 py-3 rounded font-semibold"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingMovie(null)}
                      className="bg-white/10 px-5 py-3 rounded font-semibold"
                    >
                      Cancel
                    </button>
                  </div>

                  <p className="text-xs text-gray-400">
                    Tip: If you switch to external URL, the stored video_path
                    will be cleared. If you switch to Storage, video_url will be
                    cleared.
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
